import { useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { Trash2 } from 'lucide-react'
import { directMessageApi } from '@/services/api/directMessages'
import { formatTime, getInitials, hashColor } from '@/utils/chatHelpers'
import type { DmMessage, DmParticipant } from '@/types/direct-message'

interface DmMessageBubbleProps {
  message: DmMessage
  participant?: DmParticipant
  isOwn: boolean
  conversationId: string
}

export function DmMessageBubble({ message, participant, isOwn, conversationId }: DmMessageBubbleProps) {
  const queryClient = useQueryClient()
  const [busy, setBusy] = useState(false)
  const name = participant?.name || message.sender_name || ''
  const color = hashColor(message.sender_id)

  const handleDelete = async () => {
    if (busy) return
    setBusy(true)
    try {
      await directMessageApi.deleteMessage(conversationId, message.id)
      queryClient.invalidateQueries({ queryKey: ['dm-messages', conversationId] })
      queryClient.invalidateQueries({ queryKey: ['direct-messages'] })
    } catch {
      /* keep bubble until refetch */
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className={`flex gap-2 ${isOwn ? 'flex-row-reverse' : ''}`}>
      {!isOwn && (
        <div
          className="w-7 h-7 rounded-full flex items-center justify-center text-white text-[10px] font-bold shrink-0 mt-1"
          style={{ backgroundColor: color }}
          title={name}
        >
          {getInitials(name)}
        </div>
      )}
      <div className={`max-w-[75%] group ${isOwn ? 'items-end' : ''}`}>
        {!isOwn && name && (
          <div className="text-[11px] text-gray-500 mb-0.5 px-1">{name}</div>
        )}
        <div
          className={`px-3 py-2 text-sm whitespace-pre-wrap break-words ${
            isOwn
              ? 'bg-blue-600 text-white rounded-lg rounded-tr-none'
              : 'bg-white border border-gray-200 text-gray-900 rounded-lg rounded-tl-none'
          }`}
        >
          {message.body}
        </div>
        <div className={`flex items-center gap-2 mt-0.5 px-1 ${isOwn ? 'justify-end' : ''}`}>
          <span className="text-[10px] text-gray-400">{formatTime(message.created_at)}</span>
          {isOwn && (
            <button
              type="button"
              onClick={handleDelete}
              disabled={busy}
              className="opacity-0 group-hover:opacity-100 text-red-500 hover:text-red-600 disabled:opacity-50"
              title="Löschen"
            >
              <Trash2 className="w-3 h-3" />
            </button>
          )}
        </div>
      </div>
    </div>
  )
}
